// src/vdr/ideals.ts
import * as XLSX from "xlsx";
import type { IndexRow } from "./providers";

// Accepts codes like "1", "1.1", "1.1.2" (pure digits + dots)
const CODE_RE = /^\d+(?:\.\d+)*$/;

/**
 * IdealsVDR Structure:
 * - Column A (index 0): Type ("Folder", "pdf", "xlsx", etc.)
 * - Column B (index 1): Index (e.g., "4", "4.1", "8.1.1")
 * - Column C (index 2): Name (clean names like "Project Sunrise - Phase I")
 */

// --- CSV parsing (positional columns A, B, C) ---
function parseCsv(text: string): IndexRow[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  if (!lines.length) return [];

  const rows: IndexRow[] = [];

  // Skip first line (header)
  for (let i = 1; i < lines.length; i++) {
    const cols = safeSplit(lines[i]).map(c => c.trim());

    // Column A = type (index 0), Column B = code (index 1), Column C = name (index 2)
    const typeCell = (cols[0] ?? "").toString().trim().toLowerCase();
    const code = (cols[1] ?? "").toString().trim();
    const title = (cols[2] ?? "").toString().trim();

    // Skip root folder entries
    if (typeCell === "root folder") continue;

    // Validate code
    if (!code || !CODE_RE.test(code)) continue;

    // Determine if this is a document (not a folder)
    const isDocument = typeCell !== "folder";

    rows.push({ code, title, meta: { isDocument } });
  }

  return rows;
}

// --- XLS/XLSX parsing (positional columns A, B, C) ---
async function parseXlsx(file: File): Promise<IndexRow[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  // header:1 gives us a 2D array of rows, preserving raw positions
  const table: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

  const rows: IndexRow[] = [];

  // Skip first row (header)
  for (let i = 1; i < table.length; i++) {
    const row = table[i] ?? [];
    const typeCell = String(row[0] ?? "").trim().toLowerCase(); // Column A
    const code = String(row[1] ?? "").trim();                   // Column B
    const title = String(row[2] ?? "").trim();                  // Column C

    // Skip root folder entries
    if (typeCell === "root folder") continue;

    // Validate code
    if (!code || !CODE_RE.test(code)) continue;

    // Determine if this is a document (not a folder)
    const isDocument = typeCell !== "folder";

    rows.push({ code, title, meta: { isDocument } });
  }

  return rows;
}

/**
 * Parse an IdealsVDR index file (CSV/XLS/XLSX) into normalized rows.
 * - Column A = Type ("Folder", "pdf", "xlsx", etc.)
 * - Column B = Index (hierarchical like "4", "4.1", "8.1.1")
 * - Column C = Name (clean names without prefixes)
 * - Keeps both folders and documents, marking documents with meta.isDocument
 * - Skips "Root folder" entries
 */
export async function parseIndexFile(file: File): Promise<IndexRow[]> {
  const ext = file.name.toLowerCase().split(".").pop();
  console.log(`🧾 [ideals] parseIndexFile: name="${file.name}" ext="${ext}" size=${(file as any)?.size ?? "?"}`);

  let rows: IndexRow[] = [];
  if (ext === "csv") {
    const text = await file.text();
    rows = parseCsv(text);
  } else if (ext === "xlsx" || ext === "xls") {
    rows = await parseXlsx(file);
  } else {
    // Fallback: treat as CSV text
    const text = await file.text();
    rows = parseCsv(text);
  }

  console.log(`   ↳ parsed rows: ${rows.length}`);
  if (rows.length > 0) {
    const sample = rows.slice(0, Math.min(3, rows.length));
    console.log("   ↳ sample:", sample);
  } else {
    console.warn("   ⚠️ No valid IdealsVDR rows parsed. Expected columns: A=Type, B=Index, C=Name");
  }
  return rows;
}

/**
 * Simple CSV splitter for common cases.
 * Handles basic quoted fields (reused from other parsers).
 */
function safeSplit(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}
